import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Leaf, Mountain, Scissors, Axe, Droplets, Sprout, BrickWall, Fence, Sun, Grid, Trash2, Truck, Layers, Waves, TreePine, Shield, Phone, ArrowRight } from 'lucide-react';
type Category = 'all' | 'lawn' | 'hardscape' | 'specialty';
interface Service {
  icon: React.ReactNode;
  title: string;
  description: string;
  category: Category;
  accent: string;
}
const SERVICES: Service[] = [{
  icon: <Leaf size={28} data-id="element-193" />,
  title: 'Lawn Maintenance',
  description: 'Weekly & Bi-Weekly mowing, edging, and cleanup to keep your yard looking sharp all year.',
  category: 'lawn',
  accent: '#52B788'
}, {
  icon: <Scissors size={28} data-id="element-194" />,
  title: 'Tree Trimming',
  description: 'Expert pruning and trimming to keep your trees healthy, safe, and looking great.',
  category: 'lawn',
  accent: '#52B788'
}, {
  icon: <Sprout size={28} data-id="element-195" />,
  title: 'New Sod & Seeding',
  description: 'Fresh lawn installation with premium sod or seed for a thick, lush, green yard.',
  category: 'lawn',
  accent: '#52B788'
}, {
  icon: <Sun size={28} data-id="element-196" />,
  title: 'Artificial Grass',
  description: 'Low-maintenance, always-green artificial turf installed to perfection.',
  category: 'lawn',
  accent: '#52B788'
}, {
  icon: <Droplets size={28} data-id="element-197" />,
  title: 'Irrigation Systems',
  description: 'Installation and repair of sprinkler and drip systems to keep your lawn green effortlessly.',
  category: 'lawn',
  accent: '#52B788'
}, {
  icon: <Axe size={28} data-id="element-198" />,
  title: 'Stump Removal',
  description: 'Complete stump grinding and removal — no eyesores, no tripping hazards, no mess left behind.',
  category: 'lawn',
  accent: '#52B788'
}, {
  icon: <Mountain size={28} data-id="element-199" />,
  title: 'Hardscape',
  description: 'Professional design and installation of hardscape elements that add value and beauty.',
  category: 'hardscape',
  accent: '#2D6A4F'
}, {
  icon: <Grid size={28} data-id="element-200" />,
  title: 'Pavers',
  description: 'Beautiful, durable paver installation for driveways, walkways, patios, and pool decks.',
  category: 'hardscape',
  accent: '#2D6A4F'
}, {
  icon: <BrickWall size={28} data-id="element-201" />,
  title: 'Masonry Work',
  description: 'Quality masonry for walls, borders, edging, and custom stonework built to last.',
  category: 'hardscape',
  accent: '#2D6A4F'
}, {
  icon: <Layers size={28} data-id="element-202" />,
  title: 'Garden Walls',
  description: 'Decorative and functional garden walls that add structure and curb appeal to your property.',
  category: 'hardscape',
  accent: '#2D6A4F'
}, {
  icon: <Shield size={28} data-id="element-203" />,
  title: 'Retaining Walls',
  description: 'Engineered retaining walls that prevent erosion, manage slopes, and add lasting beauty to your landscape.',
  category: 'hardscape',
  accent: '#2D6A4F'
}, {
  icon: <Fence size={28} data-id="element-204" />,
  title: 'Fencing',
  description: 'Privacy, security, and style — professional fence installation for any property.',
  category: 'specialty',
  accent: '#40916C'
}, {
  icon: <TreePine size={28} data-id="element-205" />,
  title: 'Wood Decks',
  description: 'Custom wood deck design and installation — perfect for patios, outdoor living spaces, and entertaining areas.',
  category: 'specialty',
  accent: '#40916C'
}, {
  icon: <Waves size={28} data-id="element-206" />,
  title: 'Power Washing',
  description: 'High-pressure cleaning for driveways, walkways, patios, fences, and exterior walls.',
  category: 'specialty',
  accent: '#40916C'
}, {
  icon: <Trash2 size={28} data-id="element-207" />,
  title: 'General Clean Up',
  description: 'Full exterior property cleanups — leaves, debris, overgrowth — we leave it spotless.',
  category: 'specialty',
  accent: '#40916C'
}, {
  icon: <Truck size={28} data-id="element-208" />,
  title: 'Hauling',
  description: "We haul away yard waste, debris, and junk so you don't have to deal with it.",
  category: 'specialty',
  accent: '#40916C'
}];
const TABS: {
  key: Category;
  label: string;
  count: number;
}[] = [{
  key: 'all',
  label: 'All Services',
  count: SERVICES.length
}, {
  key: 'lawn',
  label: 'Lawn & Green',
  count: SERVICES.filter(s => s.category === 'lawn').length
}, {
  key: 'hardscape',
  label: 'Hardscape & Stone',
  count: SERVICES.filter(s => s.category === 'hardscape').length
}, {
  key: 'specialty',
  label: 'Specialty',
  count: SERVICES.filter(s => s.category === 'specialty').length
}];
export function ServicesGrid() {
  const [activeTab, setActiveTab] = useState<Category>('all');
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const filtered = activeTab === 'all' ? SERVICES : SERVICES.filter(s => s.category === activeTab);
  return <section id="services" className="py-14 bg-lightGray" data-id="element-209">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-210">
        {/* Header */}
        <div className="text-center mb-10" data-id="element-211">
          <span className="text-primary font-bold tracking-wider text-sm uppercase mb-2 block" data-id="element-212">
            WHAT WE DO
          </span>
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-darkCharcoal mb-4" data-id="element-213">
            Every Exterior Service You Need
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-214">
            From weekly lawn maintenance to full hardscape installations — one
            team handles it all.
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10" data-id="element-215">
          {TABS.map(tab => <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`relative px-5 py-2.5 rounded-full font-semibold text-sm transition-all ${activeTab === tab.key ? 'bg-primary text-white shadow-md' : 'bg-white text-gray-600 hover:text-primary border border-gray-200 hover:border-primary/30'}`} data-id="element-216">
              {tab.label}
              <span className={`ml-1.5 text-xs ${activeTab === tab.key ? 'text-white/70' : 'text-gray-400'}`} data-id="element-217">
                {tab.count}
              </span>
            </button>)}
        </div>

        {/* Services Grid */}
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-218">
          <AnimatePresence mode="popLayout" data-id="element-219">
            {filtered.map((service, index) => <motion.div key={service.title} layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.25,
            delay: index * 0.03
          }} onMouseEnter={() => setHoveredIndex(index)} onMouseLeave={() => setHoveredIndex(null)} className="group relative bg-white rounded-xl p-5 border border-gray-100 cursor-default overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-primary/20 hover:-translate-y-1" data-id="element-220">
                {/* Accent bar */}
                <div className="absolute top-0 left-0 right-0 h-1 transition-all duration-300 group-hover:h-1.5" style={{
              backgroundColor: service.accent
            }} data-id="element-221" />

                <div className="flex items-start gap-4" data-id="element-222">
                  <div className="shrink-0 w-12 h-12 rounded-lg flex items-center justify-center transition-all duration-300 group-hover:scale-110" style={{
                backgroundColor: hoveredIndex === index ? service.accent : `${service.accent}15`,
                color: hoveredIndex === index ? '#ffffff' : service.accent
              }} data-id="element-223">
                    {service.icon}
                  </div>
                  <div className="min-w-0" data-id="element-224">
                    <h3 className="font-bold text-darkCharcoal text-base mb-1 group-hover:text-primary transition-colors" data-id="element-225">
                      {service.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 group-hover:text-gray-600 transition-colors" data-id="element-226">
                      {service.description}
                    </p>
                  </div>
                </div>

                {/* Hover arrow */}
                <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-x-2 group-hover:translate-x-0" data-id="element-227">
                  <ArrowRight size={16} className="text-primary" data-id="element-228" />
                </div>
              </motion.div>)}
          </AnimatePresence>
        </motion.div>

        {/* Bottom CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 text-center" data-id="element-229">
          <p className="text-darkCharcoal font-semibold" data-id="element-230">
            Not sure which service you need?
          </p>
          <div className="flex items-center gap-3" data-id="element-231">
            <a href="#contact" className="bg-primary hover:bg-primary/90 text-white font-bold py-3 px-6 rounded-md transition-colors text-sm" data-id="element-232">
              GET FREE ESTIMATE
            </a>
            <a href="tel:6192492966" className="inline-flex items-center gap-2 text-primary font-bold hover:text-primary/80 transition-colors text-sm" data-id="element-233">
              <Phone size={18} data-id="element-234" />
              (619) 249-2966
            </a>
          </div>
        </div>
      </div>
    </section>;
}